import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { label, when } from "@/lib/trust";

function Decide({ a, final, onDone }) {
  const [reason, setReason] = useState("");
  const go = async (outcome) => {
    try { await api.post(`/appeals/${a.appeal_id}/${final ? "final" : "decide"}`, { outcome, reason }); toast.success(`Appeal ${outcome}`); onDone(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid={`appeal-decide-${a.appeal_id}`}>
      <input className="field flex-1" placeholder={final ? "Owner's final reasons (min 10 characters)" : "Reasons (min 10 characters)"} value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`appeal-reason-${a.appeal_id}`} />
      <button className="btn-outline text-xs" onClick={() => go("upheld")} data-testid={`appeal-uphold-${a.appeal_id}`}>Uphold</button>
      <button className="btn-gold text-xs" onClick={() => go("overturned")} data-testid={`appeal-overturn-${a.appeal_id}`}>Overturn</button>
    </div>
  );
}

export function Appeals() {
  const { can, user } = useAuth();
  const qc = useQueryClient();
  const [status, setStatus] = useState("");
  const { data } = useQuery({ queryKey: ["staff", "appeals", status], queryFn: () => api.get("/appeals/queue", { params: { status: status || undefined } }).then((r) => r.data) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["staff", "appeals"] });
  const claim = async (id) => { try { await api.post(`/appeals/${id}/claim`); toast.success("Appeal claimed"); refresh(); } catch (err) { toast.error(formatApiError(err)); } };
  return (
    <div className="space-y-3" data-testid="staff-appeals">
      <p className="text-sm text-slate-500">The independent appeals team (staff granted <span className="font-mono">appeals.decide</span>) handles appeals for communities, leagues, moderation and verification with one process. Original decision-makers and conflicted staff are excluded. Escalated appeals go to the owner for a final decision.</p>
      <div className="flex flex-wrap gap-2">{["", "submitted", "under_review", "escalated", "upheld", "overturned"].map((s) => <button key={s || "open"} onClick={() => setStatus(s)} className={`rounded-full border px-3 py-1 text-xs ${status === s ? "border-aether-400 text-aether-200" : "border-[#2A3447] text-slate-400"}`} data-testid={`appeal-filter-${s || "open"}`}>{s ? label(s) : "open"}</button>)}</div>
      {data?.items.map((a) => {
        const mineToDecide = can("appeals.decide") && !a.my_conflict && ["submitted", "under_review"].includes(a.status) && [null, user.user_id].includes(a.assigned_to);
        return (
          <div key={a.appeal_id} className="panel space-y-2 p-4" data-testid={`appeal-row-${a.appeal_id}`}>
            <div className="flex flex-wrap justify-between gap-2"><span className="text-amber-100">{label(a.subject_type)} · {a.original.label}</span>
              <span className="font-mono text-xs uppercase text-aether-300" data-testid={`appeal-status-${a.appeal_id}`}>{label(a.status)} · @{a.appellant?.username} · assigned {a.assignee ? `@${a.assignee.username}` : "—"}</span></div>
            <p className="text-xs text-slate-400">Original decision: <strong>{label(a.original.decision)}</strong> ({when(a.original.decided_at)}) — {a.original.reason}</p>
            <p className="text-sm text-slate-300">{a.explanation}</p>
            {a.decision && <p className="text-xs text-slate-400">Team decision: {a.decision.outcome} — {a.decision.reason}</p>}
            {a.my_conflict && <p className="text-xs text-red-300">You cannot decide: {a.my_conflict}</p>}
            {mineToDecide && !a.assigned_to && <button className="btn-outline text-xs" onClick={() => claim(a.appeal_id)} data-testid={`appeal-claim-${a.appeal_id}`}>Claim</button>}
            {mineToDecide && <Decide a={a} onDone={refresh} />}
            {a.status === "escalated" && can("appeals.final") && <Decide a={a} final onDone={refresh} />}
          </div>
        );
      })}
      {data && !data.items.length && <p className="text-sm text-slate-500" data-testid="appeals-empty">No appeals in this queue.</p>}
    </div>
  );
}
